import React, { useState, useEffect } from 'react';
import ProductManager from '../components/ProductManager';
import PremiumManager from '../components/PremiumManager';
import RealtimeStats from '../components/RealtimeStats';
import DiscordWebhookManager from '../components/DiscordWebhookManager';
import RconManager from '../components/RconManager';
import PaymentSettings from '../components/PaymentSettings';
import ShopSettings from '../components/ShopSettings';
import CategoryManager from '../components/CategoryManager';
import Button from '../components/Button';
import api from '../services/api';
import { FaBoxOpen, FaCrown, FaChartLine, FaDiscord, FaServer, FaCreditCard, FaCog, FaTags } from 'react-icons/fa';

const DashboardPage = () => {
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [activeView, setActiveView] = useState(null);

  useEffect(() => {
    const fetchShops = async () => {
      try {
        const res = await api.get('/shops/my-shops'); // Endpoint to get all user's shops
        setShops(res.data);
        if (res.data.length > 0) {
          setSelectedShop(res.data[0]);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchShops();
  }, []);

  const handleShopUpdate = (updatedShop) => {
    setSelectedShop(updatedShop);
    setShops(shops.map(shop => shop._id === updatedShop._id ? updatedShop : shop));
  };

  const renderActiveView = () => {
    if (!selectedShop) return null;

    switch (activeView) {
      case 'categories':
        return <CategoryManager shopId={selectedShop._id} />;
      case 'products':
        return <ProductManager shopId={selectedShop._id} />;
      case 'premium':
        return <PremiumManager shop={selectedShop} onUpdate={handleShopUpdate} />;
      case 'stats':
        return selectedShop.premiumFeatures.analytics ? <RealtimeStats shopId={selectedShop._id} /> : <p>Ta funkcja jest dostępna tylko dla użytkowników Premium.</p>;
      case 'discord':
        return <DiscordWebhookManager shop={selectedShop} onUpdate={handleShopUpdate} />;
      case 'rcon':
        return <RconManager shop={selectedShop} onUpdate={handleShopUpdate} />;
      case 'payments':
        return <PaymentSettings shop={selectedShop} onUpdate={handleShopUpdate} />;
      case 'settings':
        return <ShopSettings shop={selectedShop} onUpdate={handleShopUpdate} />;
      default:
        return null;
    }
  };

  const Tile = ({ title, description, icon, onClick }) => (
    <div
      className="glass p-6 rounded-xl text-left hover:scale-105 transition-transform duration-300 cursor-pointer flex flex-col"
      onClick={onClick}
    >
      <div className="flex items-center mb-4">
        <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mr-4">
          {icon}
        </div>
        <h3 className="text-xl font-bold text-white">{title}</h3>
      </div>
      <p className="text-gray-300 text-sm flex-grow">{description}</p>
    </div>
  );

  const ShopSelector = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-white">Wybierz sklep</h2>
        {shops.length < 3 && (
          <Button onClick={() => window.location.href = '/create-shop'}>
            Stwórz nowy sklep
          </Button>
        )}
      </div>
      <div className="flex space-x-4 overflow-x-auto pb-4">
        {shops.map(shop => (
          <div key={shop._id} onClick={() => setSelectedShop(shop)} className={`flex flex-col items-center justify-center flex-shrink-0 cursor-pointer p-2 rounded-lg border-2 transition-all duration-300 w-32 h-32 ${selectedShop?._id === shop._id ? 'border-blue-500 bg-white/10' : 'border-transparent hover:bg-white/5'}`}>
            <img src={shop.customization?.logoUrl ? `${shop.customization.logoUrl}` : 'https://placehold.co/64'} alt={shop.name} className="w-16 h-16 bg-gray-700 rounded-full object-cover"/>
            <p className="text-center text-white w-full truncate mt-2">{shop.name}</p>
          </div>
        ))}
      </div>
    </div>
  );

  if (shops.length === 0) {
    return <p>Nie masz jeszcze żadnego sklepu. <a href="/create-shop">Stwórz nowy!</a></p>;
  }

  return (
    <div className="p-6">
      <ShopSelector />
      <h1 className="text-4xl font-bold mb-8 text-white">Panel Zarządzania Sklepem: {selectedShop?.name}</h1>

      {activeView ? (
        <div>
          <Button onClick={() => setActiveView(null)} className="mb-4">
            &larr; Powrót
          </Button>
          {renderActiveView()}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Tile
            title="Kategorie"
            description="Zarządzaj kategoriami produktów w swoim sklepie."
            icon={<FaTags className="text-2xl text-teal-400" />}
            onClick={() => setActiveView('categories')}
          />
          <Tile
            title="Produkty"
            description="Dodawaj, edytuj i usuwaj produkty w swoim sklepie."
            icon={<FaBoxOpen className="text-2xl text-blue-400" />}
            onClick={() => setActiveView('products')}
          />
          <Tile
            title="Premium"
            description="Zarządzaj subskrypcją premium i odblokuj nowe funkcje."
            icon={<FaCrown className="text-2xl text-yellow-400" />}
            onClick={() => setActiveView('premium')}
          />
          <Tile
            title="Statystyki"
            description="Śledź sprzedaż i aktywność w czasie rzeczywistym (Premium)."
            icon={<FaChartLine className="text-2xl text-green-400" />}
            onClick={() => setActiveView('stats')}
          />
          <Tile
            title="Discord"
            description="Konfiguruj powiadomienia o zakupach na swoim serwerze Discord."
            icon={<FaDiscord className="text-2xl text-indigo-400" />}
            onClick={() => setActiveView('discord')}
          />
          <Tile
            title="RCON"
            description="Połącz się z serwerem gry, aby automatycznie dostarczać produkty."
            icon={<FaServer className="text-2xl text-orange-400" />}
            onClick={() => setActiveView('rcon')}
          />
          <Tile
            title="Płatności"
            description="Skonfiguruj metody płatności, takie jak Stripe czy PayByLink."
            icon={<FaCreditCard className="text-2xl text-purple-400" />}
            onClick={() => setActiveView('payments')}
          />
          <Tile
            title="Ustawienia"
            description="Dostosuj wygląd i ogólne ustawienia swojego sklepu."
            icon={<FaCog className="text-2xl text-gray-400" />}
            onClick={() => setActiveView('settings')}
          />
        </div>
      )}
    </div>
  );
};

export default DashboardPage;